// 设置合并行列的key值，方法存取。统一取值方式
export function setMergedColRowSpanKey(rowIndex, columnIndex) {
  return rowIndex + '_' + columnIndex
}

/**
 *
 * @description 创建用于计算合并行列的方法队列
 * @export
 * @param {*} data
 * @param {*} { calculateSpanMethods, getEqualCellsMap }
 * @return {*}
 */
export function createMergeCellsTaskQueue(data, { calculateSpanMethods, getEqualCellsMap }) {
  const taskQueue = [
    // 如果是使用SpanMethods，则需要自己去计算最终需要合并的行列的数据
    (result, index) => calculateSpanMethods(result, index, data),
    // 使用配置，合并相同的合并单元格或者自动计算需要合并的相同单元格行列，推荐使用，默认合并所有相同的合并行列
    (result, index) => getEqualCellsMap(result, index, data)
  ]
  return taskQueue
}

/**
 *
 * @description 创建合并行列需要的数据，在table数据更新变化时调用
 * @export
 * @param {*} formatDataResult
 * @param {*} coreInstance
 * @return {*}
 */
export function mergeCellsData(formatDataResult, coreInstance) {
  coreInstance.store.clearEqualCellsMap()
  coreInstance.store.clearMergeRowColspanMap()
  const { getEqualCellsMap, calculateSpanMethods, usingRowColMerge } = coreInstance.store
  const taskQueue = createMergeCellsTaskQueue(formatDataResult.tableData, {
    getEqualCellsMap,
    calculateSpanMethods
  })
  if (!usingRowColMerge) {
    return formatDataResult
  }
  formatDataResult.tableData.forEach((cur, index) => {
    taskQueue.forEach(taskCallback => taskCallback(cur, index))
  })
  coreInstance.store.calculateEqualMergeCells()
  return formatDataResult
}

/**
 *
 * @desc 计算单元格数据的相同的合并行列配置
 * @param {*} data
 * @param {*} option
 */
export function calculateEqualMergeCells(data, option) {
  const { direction = 'row', beforeMerge = null } = option
  const mergeCells = []

  for (const cellValue in data) {
    const rowColMap = data[cellValue]
    if (beforeMerge && typeof beforeMerge === 'function') {
      const isMerge = beforeMerge({
        equalCellsMap: data,
        value: cellValue,
        ...rowColMap
      })
      // 如果返回false就不合并这一列
      if (!isMerge) {
        continue
      }
    }
    if (direction === 'row') {
      mergeCells.push(...createCellOptionFromCellValue(rowColMap.col, 'row', cellValue))
    } else if (direction === 'col') {
      mergeCells.push(...createCellOptionFromCellValue(rowColMap.row, 'col', cellValue))
    }
  }
  return mergeCells
}

/**
 *
 * @description 以单元格数据的维度生成连续且递增的合并行列配置项
 * @param {*} obj
 * @param {*} type
 * @param {*} cellValue
 * @return {*}
 */
function createCellOptionFromCellValue(obj, type, cellValue) {
  const cellValueMergeCells = []

  for (const index in obj) {
    // 查找当前数据中的数组集合，是否为连续递增的，是，则取出 比如 { 0: [0 ,1], 2: [2], 3: [2], 4: [2]}
    // 则可取出0中连续递增的值为 [0, 1]
    let sequences = [...findSequentialIncreasingSequences(obj[index])]
    if (!sequences.length) {
      continue
    }
    // 从连续递增的数据以及合并的方向type中，推算出对应的合并配置的起始位置，rowspan，colspan
    for (let sequencesIndex = 0; sequencesIndex < sequences.length; sequencesIndex++) {
      const sequence = sequences[sequencesIndex]
      if (sequence && sequence.length) {
        const mergeConfig = createMergeCellItem(+index, sequence[0], type, cellValue, sequence.length)
        cellValueMergeCells.push(mergeConfig)
      }
    }
  }
  return cellValueMergeCells
}

/**
 *
 * @description 计算合并列或者行的项
 * @param {*} start
 * @param {*} end
 * @param {*} type
 * @param {*} cellValue
 * @param {*} len
 * @return {*}
 */
function createMergeCellItem(start, end, type, cellValue, len) {
  if (type === 'row') {
    return { row: end, col: start, rowspan: len, colspan: 1, cellValue }
  } else if (type === 'col') {
    return { row: start, col: end, rowspan: 1, colspan: len, cellValue }
  }
}

/**
 *
 * @desc 寻找连续递增的序列
 * @param {*} arr
 * @return {*}
 */
function findSequentialIncreasingSequences(arr) {
  const sequences = []
  let sequence = []

  for (let i = 0; i < arr.length; i++) {
    if (arr[i] === arr[i - 1] + 1 || i === 0) {
      sequence.push(arr[i])
    } else {
      if (sequence.length > 1) {
        sequences.push([...sequence])
      }
      sequence = [arr[i]]
    }
  }

  if (sequence.length > 1) {
    sequences.push([...sequence])
  }

  return sequences
}

/**
 *
 * @description 构建合并行列需要的键值对
 * @param {*} index
 * @param {*} mergeSum
 * @return {*}
 */
function createMergeItem(index, mergeSum) {
  const mergeIndex = Array.from({ length: mergeSum }, (_, i) => index + i)
  const key = mergeIndex.join(',')
  return {
    key,
    value: {
      mergeSum,
      mergeIndex,
      mergeStartIndex: mergeIndex[0],
      mergeEndIndex: mergeIndex[mergeIndex.length - 1]
    }
  }
}

/**
 *
 * @description 转换mergeCells为合并行列需要的数据
 * @param {*} [mergeCells=[]]
 * @return {*}
 */
export function transformMergeCellsToRowColSpanResult(mergeCells = []) {
  const mergeRowspanMaps = {}
  const mergeColspanMaps = {}
  const mergedColRowSpanResult = {}

  for (const { row, col, rowspan, colspan } of mergeCells) {
    const rowColKey = setMergedColRowSpanKey(row, col)
    mergedColRowSpanResult[rowColKey] = { rowspan, colspan }

    if (rowspan > 1) {
      const { key, value } = createMergeItem(row, rowspan)
      mergeRowspanMaps[key] = value
      for (let diff = 1; diff < rowspan; diff++) {
        const rowColKey = setMergedColRowSpanKey(row + diff, col)
        mergedColRowSpanResult[rowColKey] = { rowspan: 0, colspan: 0 }
      }
    }

    if (colspan > 1) {
      const { key, value } = createMergeItem(col, colspan)
      mergeColspanMaps[key] = value
      for (let diff = 1; diff < colspan; diff++) {
        const rowColKey = setMergedColRowSpanKey(row, col + diff)
        mergedColRowSpanResult[rowColKey] = { rowspan: 0, colspan: 0 }
      }
    }
  }

  return {
    mergedColRowSpanResult,
    mergeRowspanMaps,
    mergeColspanMaps
  }
}



// WEBPACK FOOTER //
// ./src/public/components/kye-table2/store/config/merge-cell.js